import fetchApiWithAuth from "./fetch";
import fetchApi from "./fetch";

type AnswerMain = {
  status: string;
  text: string;
  created_at: string;
  published_at: string | null;
  generated: boolean;
  selected_template_id?: number | null;
  selected_template_score?: number | null;
  reset_at?: string | null;
  reset_error?: string | null;
};

type ReviewMain = {
  id: number;
  external_id: string;
  marketplace: string;
  sku: string;
  rating: number;
  text: { comment: string; positive: string; negative: string };
  author_name: string;
  published_at: string;
  brand: string;
  article: string;
  productName: string;
  media_links?: { photos?: string[] };
  status?: string;
};

export type ReviewsProps = ReviewMain & { answer?: AnswerMain };

/** Ответ POST /api/reviews (список с пагинацией). */
export type ReviewsListResponse = {
  data: ReviewsProps[];
  total: number;
  total_page: number;
  /** true — total/total_page от EXPLAIN (оценка); false — точный COUNT(*) */
  total_is_estimate: boolean;
  /** Есть ли ещё страница (надёжнее оценки total при больших таблицах) */
  hasMore?: boolean;
};

export const getReviewsByFilter = async (props: {
  marketplace?: string[];
  rating?: number[];
  article?: string;
  /** Точное совпадение артикула (карточка товара). */
  articleExact?: boolean;
  status?: string[];
  sorting: string;
  page: number;
  tag_ids?: number[];
  /** Точный COUNT (медленно на больших выборках); по умолчанию сервер отдаёт оценку через EXPLAIN */
  exactTotal?: boolean;
  /** @deprecated в пользу exactTotal */
  includeTotal?: boolean;
}) => {
  const result = await fetchApiWithAuth("/api/reviews", {
    method: "POST",
    body: JSON.stringify(props),
  });

  return (await result.json()) as ReviewsListResponse;
};

export const updateReview = async (props: {
  id: number;
  data: Partial<ReviewsProps>;
}) => {
  const result = await fetchApiWithAuth(`/api/reviews/${props.id}`, {
    method: "PATCH",
    body: JSON.stringify(props.data),
  });

  return (await result.json()) as ReviewsProps;
};

export type AnswerQueueStats = {
  marketplace: string;
  counts: Record<string, number>;
  capped?: Record<string, boolean>;
  pendingSend: number;
  sendingNow: number;
  sendJobScheduled: boolean;
  cached?: boolean;
};

export const getAnswerQueueStats = async (
  marketplace = "ozon",
): Promise<AnswerQueueStats> => {
  const result = await fetchApiWithAuth(
    `/api/reviews/answer-queue-stats?marketplace=${encodeURIComponent(marketplace)}`,
  );
  return result.json();
};

export type Variants = {
  name: string;
  templateText: string;
  error?: string;
  text?: string;
}[];

export const getAnswerVariantsById = async (props: { id: number }) => {
  const result = await fetchApiWithAuth(
    `/api/reviews/${props.id}/answer_variants`,
    {
      method: "GET",
    }
  );

  return (await result.json()) as Variants;
};

export type BadGoods = {
  article: string;
  rating: number;
  error: string;
  count: number;
};

export const getBadGoods = async () => {
  const result = await fetchApiWithAuth(`/api/reviews/bad_goods`, {
    method: "GET",
  });

  return (await result.json()) as BadGoods[];
};

export type FilterOptions = {
  marketplaces: string[];
};

export const getFilterOptions = async () => {
  const result = await fetchApiWithAuth(`/api/reviews/filter_options`, {
    method: "GET",
  });

  return (await result.json()) as FilterOptions;
};

export type AutoReplyReevaluationStats = {
  checked: number;
  betterTemplateFound: number;
  regenerated: number;
  unchanged: number;
  skipped: number;
  errors: number;
  unavailable?: boolean;
};

export type ReevaluateAutoRepliesStart = {
  jobId: string;
  alreadyRunning: boolean;
};

export type ReevaluateAutoRepliesStatus = {
  jobId: string;
  state: string;
  stats: AutoReplyReevaluationStats | null;
  failedReason: string | null;
  error?: string;
};

export const startReevaluateAutoReplies = async () => {
  const result = await fetchApiWithAuth("/api/reviews/reevaluate-auto-replies", {
    method: "POST",
    body: JSON.stringify({}),
  });
  return (await result.json()) as ReevaluateAutoRepliesStart;
};

export const getReevaluateAutoRepliesStatus = async (jobId: string) => {
  const result = await fetchApiWithAuth(
    `/api/reviews/reevaluate-auto-replies/${encodeURIComponent(jobId)}`,
  );
  return (await result.json()) as ReevaluateAutoRepliesStatus;
};

export const addJob = async (data: {
  name: string;
  data: Record<string, string>;
}) => {
  return await fetchApiWithAuth(`/api/reviews/jobs`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

/** Очистить архив: все завершённые и ошибочные задачи */
export const clearQueueArchive = async (queue?: string) => {
  const result = await fetchApiWithAuth("/api/reviews/jobs/clear", {
    method: "POST",
    body: JSON.stringify(queue ? { queue } : {}),
  });
  const data = await result.json();
  return data;
};

/** Очистить ожидающие задачи (ещё не взятые в работу) */
export const clearQueueWaiting = async (
  queue: string
): Promise<{ ok?: boolean; error?: string }> => {
  const result = await fetchApiWithAuth("/api/reviews/jobs/clear-waiting", {
    method: "POST",
    body: JSON.stringify({ queue }),
  });
  return result.json();
};

/** @deprecated use clearQueueArchive(queueName) */
export const clearQueue = (queue?: string) => clearQueueArchive(queue);

/** @deprecated use clearQueueArchive() */
export const clearJobs = async () => clearQueueArchive();

export const removeJob = async (
  queue: string,
  jobId: string
): Promise<{ ok?: boolean; error?: string }> => {
  const result = await fetchApiWithAuth("/api/reviews/jobs/remove", {
    method: "POST",
    body: JSON.stringify({ queue, jobId }),
  });
  return result.json();
};

export const getJobsStatus = async () => {
  const result = await fetchApiWithAuth("/api/reviews/jobs/status");
  const data = await result.json();
  return data as JobStatus;
};

export const JOBS_PAGE_SIZE = 20;

export type JobsListResponse = { jobs: Job[]; total: number };

export const getWaitingJobs = async (
  page = 1,
  limit = JOBS_PAGE_SIZE
): Promise<JobsListResponse> => {
  const result = await fetchApiWithAuth(
    `/api/reviews/jobs/waiting?page=${page}&limit=${limit}`
  );
  const data = await result.json();
  return data as JobsListResponse;
};

export const getActiveJobs = async (
  page = 1,
  limit = JOBS_PAGE_SIZE
): Promise<JobsListResponse> => {
  const result = await fetchApiWithAuth(
    `/api/reviews/jobs/active?page=${page}&limit=${limit}`
  );
  const data = await result.json();
  return data as JobsListResponse;
};

export const getDelayedJobs = async (
  page = 1,
  limit = JOBS_PAGE_SIZE
): Promise<JobsListResponse> => {
  const result = await fetchApiWithAuth(
    `/api/reviews/jobs/delayed?page=${page}&limit=${limit}`
  );
  const data = await result.json();
  return data as JobsListResponse;
};

export const getFailedJobs = async (
  page = 1,
  limit = JOBS_PAGE_SIZE
): Promise<JobsListResponse> => {
  const result = await fetchApiWithAuth(
    `/api/reviews/jobs/failed?page=${page}&limit=${limit}`
  );
  const data = await result.json();
  return data as JobsListResponse;
};

export const getCompletedJobs = async (
  page = 1,
  limit = JOBS_PAGE_SIZE
): Promise<JobsListResponse> => {
  const result = await fetchApiWithAuth(
    `/api/reviews/jobs/completed?page=${page}&limit=${limit}`
  );
  const data = await result.json();
  return data as JobsListResponse;
};

export const getLastRunTimes = async (): Promise<Record<string, string>> => {
  const result = await fetchApiWithAuth("/api/reviews/jobs/last-run-times");
  return result.json();
};

export type QueueCounts = {
  waiting: number;
  delayed: number;
  active: number;
  completed: number;
  failed: number;
};

export const getQueuesDashboard = async (): Promise<{
  queues: Record<string, QueueCounts>;
}> => {
  const result = await fetchApiWithAuth("/api/reviews/jobs/queues-dashboard");
  return result.json();
};

export type WorkerStatus = { name: string; paused: boolean };

export const getWorkersStatus = async (): Promise<{
  workers: WorkerStatus[];
  error?: string;
}> => {
  const result = await fetchApiWithAuth("/api/reviews/workers/status");
  return result.json();
};

export const pauseWorker = async (
  name: string
): Promise<{ ok?: boolean; name?: string; paused?: boolean; error?: string }> => {
  const result = await fetchApiWithAuth(
    `/api/reviews/workers/${encodeURIComponent(name)}/pause`,
    { method: "POST", body: JSON.stringify({}) }
  );
  return result.json();
};

export const resumeWorker = async (
  name: string
): Promise<{ ok?: boolean; name?: string; paused?: boolean; error?: string }> => {
  const result = await fetchApiWithAuth(
    `/api/reviews/workers/${encodeURIComponent(name)}/resume`,
    { method: "POST", body: JSON.stringify({}) }
  );
  return result.json();
};

export const getLastReviewKeys = async (): Promise<Record<string, string>> => {
  const result = await fetchApiWithAuth("/api/reviews/last-review/keys");
  return result.json();
};

export const clearAllLastReview = async (): Promise<{
  success: boolean;
  message: string;
  deletedCount: number;
  deletedKeys: string[];
}> => {
  const result = await fetchApiWithAuth("/api/reviews/last-review/clear-all", {
    method: "POST",
  });
  return result.json();
};

export const clearSpecificLastReview = async (
  key: string
): Promise<{
  success: boolean;
  message: string;
}> => {
  const result = await fetchApiWithAuth(
    `/api/reviews/last-review/clear/${encodeURIComponent(key)}`,
    {
      method: "POST",
    }
  );
  return result.json();
};

export type JobStatus = {
  waiting: number;
  delayed: number;
  active: number;
  completed: number;
  failed: number;
};

export type Job = {
  id: string;
  name: string;
  data: any;
  timestamp: number;
  queue?: string;
  delay?: number;
  runAt?: number;
  priority?: number;
  finishedOn?: number;
  processedOn?: number;
  returnvalue?: any;
  failedReason?: string;
};

export type Log = {
  id: number;
  created_at: string;
  source: string;
  type: string;
  message: string;
};

export type DelayJobsLogsResponse = {
  logs: Log[];
};
